import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, EmptyState, RowsSkeletonLoader } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetLedgerWalletTransactions } from 'hooks/queries/Ledger/useGetLedgerWalletTransactions'
import { useRewardsPortfoliosQuery } from 'hooks/queries/Rewards/useRewardsPortfoliosQuery'
import { useRewardsProductsQuery } from 'hooks/queries/Rewards/useRewardsProductsQuery'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { RewardsEmbeddedTabs } from './components/embedded/RewardsEmbeddedTabs'
import { RewardCompoundedCalculator } from './components/RewardCompoundedCalculator'
import { RewardsAssets } from './components/RewardsAssets'
import { RewardsDashboard } from './components/RewardsDashboard'
import { RewardsNoWallets } from './components/RewardsNoWallets'
import { RewardsPageHeader } from './components/RewardsPageHeader'
import { RewardsSheetProvider } from './context/RewardsSheetProvider'
import useRewardsTracking from './hooks/useRewardsTracking'
import { useRewardsWallets } from './hooks/useRewardsWallets'
import { useTransactionsPagination } from './hooks/useTransactionsPagination'

export const Rewards = () => {
  const { t } = useTranslation()
  const { trackPageView } = useRewardsTracking()
  const { enableEmbeddedRewards } = useFeatureFlags()
  const { accountCapabilities } = useAccountsContext()
  const {
    isEmbeddedCustomerCompanyCapabilityEnabled,
    isEmbeddedCustomerIndividualCapabilityEnabled,
  } = accountCapabilities || {}
  const isEmbeddedPartner =
    isEmbeddedCustomerCompanyCapabilityEnabled || isEmbeddedCustomerIndividualCapabilityEnabled

  useEffect(() => {
    trackPageView('home')
  }, [])

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTransactionsPagination()

  const { data: transactionsData, isLoading: isLoadingTransactions } =
    useGetLedgerWalletTransactions({
      categories: ['EARNING'],
      page,
      size: rowsPerPage,
    })

  const transactions = transactionsData?.content ?? []

  const { paging } = usePaginationPagingPersist(transactionsData?.pagination)
  const showPagination = transactions.length > 0 && !isLoadingTransactions

  const {
    data: rewardsProductsData,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useRewardsProductsQuery()
  const rewardsProducts = rewardsProductsData?.content ?? []
  const rewardsProductCurrencies =
    rewardsProducts.map(product => product?.currency)?.filter(Boolean) ?? []

  const {
    data: rewardsPortfolios,
    isLoading: isLoadingPortfolios,
    isError: isErrorPortfolios,
  } = useRewardsPortfoliosQuery()
  const userPortfolios = rewardsPortfolios?.content ?? []
  const hasPortfolios = userPortfolios.length > 0
  const hasAnyAmountToWithdraw = userPortfolios.some(
    portfolio => Number(portfolio.balance.available) > 0
  )

  const { rewardsWallets, isLoadingWallets } = useRewardsWallets(rewardsProductCurrencies)
  const hasWalletsForRewards = rewardsWallets?.length > 0

  const isLoading = isLoadingProducts || isLoadingPortfolios || isLoadingWallets

  const showEmbeddedTabs = !!enableEmbeddedRewards && isEmbeddedPartner

  if (isLoading) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (isErrorProducts || isErrorPortfolios) {
    return (
      <EmptyState
        testId="rewards-products-error-state"
        title={t('defaultError')}
        LottiePlayer={<Lottie src="connection-error" />}
      />
    )
  }

  if (!rewardsProducts.length) {
    return (
      <EmptyState
        testId="rewards-products-empty-state"
        title={t('rewards.noProductsTitle')}
        description={t('rewards.noProductsDescription')}
        LottiePlayer={<Lottie src="no-data" />}
      />
    )
  }

  if (!hasWalletsForRewards) {
    return (
      <RewardsSheetProvider userPortfolios={userPortfolios} rewardsProducts={rewardsProducts}>
        <RewardsPageHeader
          rewardsProducts={rewardsProducts}
          showWithdrawButton={false}
          showDepositButton={false}
        />
        <RewardsEmbeddedTabs showEmbeddedTabs={showEmbeddedTabs}>
          <RewardsNoWallets rewardsProducts={rewardsProducts} />
        </RewardsEmbeddedTabs>
      </RewardsSheetProvider>
    )
  }

  if (!hasPortfolios) {
    return (
      <RewardsSheetProvider userPortfolios={userPortfolios} rewardsProducts={rewardsProducts}>
        <RewardsPageHeader
          rewardsProducts={rewardsProducts}
          showWithdrawButton={false}
          showDepositButton={true}
        />
        <RewardsEmbeddedTabs showEmbeddedTabs={showEmbeddedTabs}>
          <Box flexCol gap={16}>
            <RewardsAssets title={t('rewards.allocateToStartEarning')} layout="grid" />
            <RewardCompoundedCalculator />
          </Box>
        </RewardsEmbeddedTabs>
      </RewardsSheetProvider>
    )
  }

  return (
    <RewardsSheetProvider userPortfolios={userPortfolios} rewardsProducts={rewardsProducts}>
      <RewardsPageHeader
        rewardsProducts={rewardsProducts}
        showWithdrawButton={hasAnyAmountToWithdraw}
        showDepositButton={true}
      />

      <RewardsEmbeddedTabs showEmbeddedTabs={showEmbeddedTabs}>
        <RewardsDashboard
          transactions={transactions}
          isLoadingTransactions={isLoadingTransactions}
          showPagination={showPagination}
          page={page}
          rowsPerPage={rowsPerPage}
          paging={paging}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </RewardsEmbeddedTabs>
    </RewardsSheetProvider>
  )
}
